import { useCallback } from 'react';
import { Mic } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { cn } from '@/lib/utils';

interface VoiceDictateButtonProps {
  /** Called with the final recognized text. */
  onTranscript: (text: string) => void;
  /** Language override (BCP-47). Defaults to i18n locale. */
  lang?: string;
  /** Button size. */
  size?: 'sm' | 'default' | 'lg' | 'icon';
  className?: string;
}

const ERROR_MESSAGES: Record<string, { fr: string; en: string }> = {
  'not-allowed': {
    fr: 'Acces au microphone refuse. Verifiez les permissions de votre navigateur.',
    en: 'Microphone access denied. Check your browser permissions.',
  },
  'audio-capture': {
    fr: 'Aucun microphone detecte.',
    en: 'No microphone detected.',
  },
  network: {
    fr: 'Connexion requise pour la reconnaissance vocale.',
    en: 'Internet connection required for voice recognition.',
  },
};

export function VoiceDictateButton({
  onTranscript,
  lang,
  size = 'icon',
  className,
}: VoiceDictateButtonProps) {
  const { i18n, t } = useTranslation('common');
  const isFr = i18n.language.startsWith('fr');

  const handleError = useCallback(
    (error: string) => {
      const messages = ERROR_MESSAGES[error];
      if (messages) {
        toast.error(isFr ? messages.fr : messages.en);
      } else {
        toast.error(isFr ? 'Erreur de reconnaissance vocale.' : 'Voice recognition error.');
      }
    },
    [isFr],
  );

  const { isListening, isSupported, startListening, stopListening } = useSpeechRecognition({
    lang,
    continuous: false,
    interimResults: true,
    onResult: (text, isFinal) => {
      if (isFinal && text.trim()) {
        onTranscript(text.trim());
      }
    },
    onError: handleError,
  });

  // Hide entirely when unsupported.
  if (!isSupported) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      onClick={isListening ? stopListening : startListening}
      aria-label={
        isListening
          ? (t('voice_stop', 'Arreter') as string)
          : (t('voice_start', 'Dicter') as string)
      }
      className={cn(
        'relative shrink-0',
        isListening && 'text-red-500 hover:text-red-600',
        className,
      )}
    >
      <Mic className="size-4" />
      {isListening && (
        <span className="absolute inset-0 animate-ping rounded-md bg-red-400/20 pointer-events-none" />
      )}
    </Button>
  );
}
